import { useInView } from 'framer-motion'
import { mergeRefs, mergeClasses } from 'minimal-shared/utils'
import {
  useRef,
  useState,
  forwardRef,
  useCallback,
  startTransition
} from 'react'

import { imageClasses } from './classes'
import { ImageImg, ImageRoot, ImageOverlay, ImagePlaceholder } from './styles'

// ----------------------------------------------------------------------

const DEFAULT_DELAY = 0
const DEFAULT_EFFECT = {
  style: 'blur',
  duration: 300,
  disabled: false
}

export const Image = forwardRef((props, ref) => {
  const {
    sx,
    src,
    icon,
    ratio,
    onLoad,
    effect,
    alt = '',
    slotProps,
    className,
    viewportOptions,
    disablePlaceholder,
    visibleByDefault = true,
    delayTime = DEFAULT_DELAY,
    ...other
  } = props

  const localRef = useRef(null)
  const [isLoaded, setIsLoaded] = useState(false)

  const isInView = useInView(localRef, {
    once: true,
    ...viewportOptions
  })

  const handleImageLoad = useCallback(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setIsLoaded(true)
        onLoad?.()
      })
    }, delayTime)

    return () => clearTimeout(timer)
  }, [delayTime, onLoad])

  const finalEffect = {
    ...DEFAULT_EFFECT,
    ...effect
  }

  const shouldRenderImage = visibleByDefault || isInView
  const showPlaceholder = !visibleByDefault && !isLoaded && !disablePlaceholder

  const renderComponents = {
    overlay: () =>
      slotProps?.overlay && (
        <ImageOverlay className={imageClasses.overlay} {...slotProps.overlay} />
      ),
    placeholder: () =>
      showPlaceholder && (
        <ImagePlaceholder
          className={imageClasses.placeholder}
          {...slotProps?.placeholder}
        />
      ),
    image: () =>
      icon ? (
        icon
      ) : (
        <ImageImg
          src={src}
          alt={alt}
          onLoad={handleImageLoad}
          className={imageClasses.img}
          {...slotProps?.img}
        />
      )
  }

  return (
    <ImageRoot
      ref={mergeRefs([localRef, ref])}
      effect={
        visibleByDefault || finalEffect.disabled ? undefined : finalEffect
      }
      className={mergeClasses([imageClasses.root, className], {
        [imageClasses.state.loaded]: !visibleByDefault && isLoaded
      })}
      sx={[
        {
          '--aspect-ratio': ratio,
          ...(!!ratio && { width: 1 })
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
      {...other}
    >
      {renderComponents.overlay()}
      {renderComponents.placeholder()}
      {shouldRenderImage && renderComponents.image()}
    </ImageRoot>
  )
})
